import { ACCESS_TOKEN, REFRESH_TOKEN, ROLES, APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import { markNotificationPromptPendingAfterLogin } from "../../utils/notificationPromptStorage";
import {
  CREATE_USER_URL,
  DOCUMENT_UPLOAD_URL,
  GET_DEGREE_MASTER_DATA,
  GET_MASTER_DATA,
  GET_OTP_URL,
  GET_USER_DETAILS,
  GET_USER_PROFILE_DETAILS,
  IMAGE_UPLOAD_URL,
  USER_CERTIFICATE_EDIT_URL,
  USER_CERTIFICATE_UPDATE_URL,
  USER_EDUCATION_EDIT_URL,
  USER_EDUCATION_UPDATE_URL,
  USER_EXPERIENCE_EDIT_URL,
  USER_EXPERIENCE_UPDATE_URL,
  USER_LOGIN_URL,
  USER_PREFERENCE_EDIT_URL,
  USER_PREFERENCE_UPDATE_URL,
  USER_PROFILE_DETAILS_UPDATE_URL,
  USER_PROFILE_UPDATE_URL,
  USER_PROFILE_URL,
  USER_PROFILE_EDIT_URL,
  USER_SIGNUP_URL,
  USER_ACCOUNT_DELETE_URL,
  VERIFY_OTP,
  ASSESSMENT_READ_URL,
  ASSESSMENT_SAVE_URL,
  ASSESSMENT_PERSONALITY_TYPE_URL,
  ASSESSMENT_TPOIC_SCORE_URL,
  ASSESSMENT_USER_BAND_URL,
  SAVE_USER_VIDEOS_URL,
  GET_USER_VIDEOS_URL,
  GET_VIDEO_QUESTIONS_URL,
  DELETE_USER_VIDEO,
  B2B_SCORE_BAND,
  B2B_TOPIC_SCORE,
  B2B_USER_PERSONALITY,
  REFRESH_TOKEN_URL,
  LOGOUT_URL,
  REPLACE_USER_VIDEO,
  GET_PRESIGNED_VIDEO_URL,
} from "../api/config";
import API_MIDDLEWARE from "../api/middleware";
import {
  SET_APPLY_BLOCKED,
  CLEAR_APPLY_BLOCKED,
  SET_ERROR_MESSAGE,
  SET_LOADING_STATE,
  SET_PERSONALITY,
  SET_PROMPTS,
  SET_QUESTIONS,
  SET_SCORE_BAND,
  SET_SUCCESS_MESSAGE,
  SET_TOPIC_SCORE,
  SET_USER_DETAILS,
  SET_USER_INFO,
  SET_USER_VIDEOS,
  SET_VIDEO_QUESTIONS,
  UPDATE_PROMPT,
} from "./actionTypes";
import { beginAjaxCall, endAjaxCall } from "./ajaxStatusActions";
import store from "../store";
import type { ApplyBlockedPayload } from "../../utils/jobApplyValidation";

export type ApplyBlockedState = ApplyBlockedPayload;

export const setErrorMessage = (payload: string): any => {
  return { type: SET_ERROR_MESSAGE, payload };
};

export const setApplyBlocked = (payload: ApplyBlockedState): any => {
  return { type: SET_APPLY_BLOCKED, payload };
};

export const clearApplyBlocked = (): any => {
  return { type: CLEAR_APPLY_BLOCKED };
};

export const setSuccessMessage = (payload: string): any => {
  return { type: SET_SUCCESS_MESSAGE, payload };
};

export const setLoadingState = (payload: boolean): any => {
  return { type: SET_LOADING_STATE, payload };
};

export const setUserInfo = (payload: any): any => {
  return { type: SET_USER_INFO, payload };
};

type DemographicsPatch = {
  dob?: string | null;
  gender?: string | null;
  languages?: string[] | null;
  city?: string | null;
  profilePicturePath?: string | null;
  profileImage?: string | null;
};

export const patchProfilePictureInStore = (pictureUrl: string | null | undefined): any => {
  return patchDemographicsInStore({
    profilePicturePath: pictureUrl,
    profileImage: pictureUrl,
  });
};

export const patchDemographicsInStore = (patch: DemographicsPatch): any => {
  return (dispatch: any) => {
    const state = store.getState();
    const currentUserWrap = state?.app?.user || {};
    const currentProfile = state?.app?.userProfile || {};
    const userPatch: Record<string, unknown> = {};
    const profilePatch: Record<string, unknown> = {};

    if (patch.dob != null) {
      userPatch.dob = patch.dob;
      profilePatch.dob = patch.dob;
    }
    if (patch.gender != null) {
      userPatch.gender = patch.gender;
      profilePatch.gender = patch.gender;
    }
    if (patch.languages != null) {
      userPatch.languages = patch.languages;
      profilePatch.languages = patch.languages;
    }
    if (patch.city != null) {
      userPatch.city = patch.city;
      profilePatch.city = patch.city;
    }
    if (patch.profilePicturePath != null) {
      userPatch.profilePicturePath = patch.profilePicturePath;
      userPatch.profileImage = patch.profileImage ?? patch.profilePicturePath;
      profilePatch.profilePicturePath = patch.profilePicturePath;
      profilePatch.profileImage = patch.profileImage ?? patch.profilePicturePath;
    } else if (patch.profileImage != null) {
      userPatch.profileImage = patch.profileImage;
      userPatch.profilePicturePath = patch.profileImage;
      profilePatch.profileImage = patch.profileImage;
      profilePatch.profilePicturePath = patch.profileImage;
    }

    if (Object.keys(userPatch).length === 0) return;

    dispatch(
      setUserInfo({
        ...currentUserWrap,
        user: {
          ...currentUserWrap.user,
          ...userPatch,
        },
      })
    );
    dispatch({
      type: SET_USER_DETAILS,
      payload: {
        ...currentProfile,
        ...profilePatch,
      },
    });
  };
};

/** Refresh user detail + profile from API without toggling the global loader. */
export const refreshUserDemographics = (): any => {
  return async (dispatch: any) => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);
      const headers = { "Content-Type": "application/json" };

      const [detailRes, profileRes] = await Promise.all([
        API_MIDDLEWARE(USER_PROFILE_URL, "GET", headers),
        API_MIDDLEWARE(GET_USER_PROFILE_DETAILS, "GET", headers),
      ]);

      dispatch(
        setUserInfo({
          accessToken,
          refreshToken,
          user: detailRes?.result,
        })
      );
      dispatch({ type: SET_USER_DETAILS, payload: profileRes?.result });
    } catch {
      // Keep existing store values if refresh fails.
    }
  };
};

export const userLogin = (
  payload: { userName: string; countryCode: string; otp: string },
  onSuccess: (res: any) => void,
  onError = () => {}
): any => {
  return (dispatch: any) => {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_LOGIN_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        const { accessToken, refreshToken } = res.result;
        localStorage.setItem(ACCESS_TOKEN, accessToken);
        localStorage.setItem(REFRESH_TOKEN, refreshToken);
        localStorage.setItem(ROLES, res?.result.roles);
        markNotificationPromptPendingAfterLogin();
        dispatch(setUserInfo(res?.result));
        dispatch(setSuccessMessage(res?.message ?? "Logged In"));
        onSuccess(res?.result);
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const refreshAPI = (
  onSuccess: (res: any) => void,
  onError = () => {}
): any => {
  return (dispatch: any) => {
    const payload = {
      "phoneNumber" : "9090909090",
      "refreshToken" : localStorage.getItem(ACCESS_TOKEN),
    };
    API_MIDDLEWARE(REFRESH_TOKEN_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        const { accessToken, refreshToken } = res.result;
        localStorage.setItem(ACCESS_TOKEN, accessToken);
        localStorage.setItem(REFRESH_TOKEN, refreshToken);
        dispatch(setUserInfo(res?.result));
        dispatch(setSuccessMessage(res?.message ?? "Logged In"));
        onSuccess(res?.result);
        dispatch(endAjaxCall());
        onSuccess(res?.result);
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const logoutClear = (
  onSuccess: (res: any) => void,
  onError = () => {}
): any => {
  return () => {
    const payload = {
      "refreshToken" : localStorage.getItem(REFRESH_TOKEN),
    };
    API_MIDDLEWARE(LOGOUT_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        localStorage.clear();
        onSuccess(res?.result);
      })
      .catch((err) => {
        onError();
        console.log(err);
      });
  };
};

export const deleteUserAccount = (onSuccess: () => void, onError?: () => void): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_ACCOUNT_DELETE_URL, "POST", {}, undefined, true)
      .then((res: any) => {
        dispatch(endAjaxCall());
        dispatch(setSuccessMessage(res?.message ?? "Account deleted successfully"));
        localStorage.clear();
        onSuccess();
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError?.();
      });
  };
};

export const userSignup = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_SIGNUP_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        // const { accessToken, refreshToken } = res.result;
        // localStorage.setItem(ACCESS_TOKEN, accessToken);
        // localStorage.setItem(REFRESH_TOKEN, refreshToken);
        dispatch(setUserInfo(res?.result));
        dispatch(setSuccessMessage(res?.message ?? "Success"));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const createUser = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(CREATE_USER_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const fetchUserProfile = (
  onSuccess: () => void = () => {},
  onError: () => void = () => {}
): any => {
  return async (dispatch: any) => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);

      dispatch(beginAjaxCall());

      const res = await API_MIDDLEWARE(
        USER_PROFILE_URL,
        "GET",
        { "Content-Type": "application/json" }
      );

      const UserData = {
        accessToken,
        refreshToken,
        user: res.result,
      };

      // ✅ wait for state update
      await dispatch(setUserInfo(UserData));

      dispatch(endAjaxCall());

      // ✅ now safely run callback
      onSuccess();

      return UserData;
    } catch (err: any) {
      dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
      dispatch(endAjaxCall());
      onError();
      throw err;
    }
  };
};


export const updateUserProfile = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return async (dispatch: any) => {
    dispatch(beginAjaxCall());
    try {
      const res = await API_MIDDLEWARE(USER_PROFILE_UPDATE_URL, "POST", { "Content-Type": "application/json" }, payload);
      const pictureUrl = payload.profilePicturePath ?? payload.profileImage;
      const hasPictureUpdate = pictureUrl != null;
      const hasDemographicFields = [payload.dob, payload.gender, payload.languages, payload.city].some(
        (value) => value != null
      );

      if (hasPictureUpdate) {
        dispatch(patchProfilePictureInStore(pictureUrl));
      }

      if (hasDemographicFields) {
        dispatch(
          patchDemographicsInStore({
            dob: payload.dob,
            gender: payload.gender,
            languages: payload.languages,
            city: payload.city,
          })
        );
        await dispatch(refreshUserDemographics());
        dispatch(
          patchDemographicsInStore({
            dob: payload.dob,
            gender: payload.gender,
            languages: payload.languages,
            city: payload.city,
          })
        );
        if (hasPictureUpdate) {
          dispatch(patchProfilePictureInStore(pictureUrl));
        }
      } else if (hasPictureUpdate) {
        // Photo-only update: avoid profile/read refresh wiping the new image before CDN/API propagates.
        try {
          const accessToken = localStorage.getItem(ACCESS_TOKEN);
          const refreshToken = localStorage.getItem(REFRESH_TOKEN);
          const detailRes = await API_MIDDLEWARE(USER_PROFILE_URL, "GET", { "Content-Type": "application/json" });
          dispatch(
            setUserInfo({
              accessToken,
              refreshToken,
              user: detailRes?.result,
            })
          );
        } catch {
          // Keep the locally patched picture if detail read fails or lags.
        }
        dispatch(patchProfilePictureInStore(pictureUrl));
      } else {
        await dispatch(refreshUserDemographics());
      }

      dispatch(setSuccessMessage(res?.message ?? "Profile updated successfully!"));
      dispatch(endAjaxCall());
      onSuccess();
    } catch (err: any) {
      dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      dispatch(endAjaxCall());
      onError();
    }
  };
};

export const updateUserProfileDetails = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_PROFILE_DETAILS_UPDATE_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const updateUserCertificate = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_CERTIFICATE_UPDATE_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const editUserCertificate = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_CERTIFICATE_EDIT_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const updateUserExperience = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_EXPERIENCE_UPDATE_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const editUserExperience = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_EXPERIENCE_EDIT_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const updateUserEducation = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_EDUCATION_UPDATE_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const editUserEducation = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_EDUCATION_EDIT_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const updateUserPreference = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_PREFERENCE_UPDATE_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const editUserPreference = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_PREFERENCE_EDIT_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? ""));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export type SendOtpParams = {
  /** National number only (no country calling code), e.g. Indian 10-digit mobile */
  nationalNumber: string;
  /** Dial country calling code digits without +, e.g. "91" */
  countryCallingCode: string;
};

export const getOTP = (params: SendOtpParams, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    const body = { countryCode: params.countryCallingCode };
    API_MIDDLEWARE(GET_OTP_URL(params.nationalNumber), "POST", { "Content-Type": "application/json" }, body)
      .then((res) => {
        dispatch(setSuccessMessage(res?.message ?? "OTP sent"));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const verifyOTP = (
  payload: { phoneNumber: string; countryCode: string; otp: string },
  onSuccess = () => {},
  onError = () => {}
): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(VERIFY_OTP, "POST", { "Content-Type": "application/json" }, payload)
      .then(() => {
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};

export const getMasterData = (field: string, type: string, query?: string): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(GET_MASTER_DATA(field, query), "GET", { "Content-Type": "application/json" })
      .then((res) => {
        dispatch({ type, payload: res?.result });
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const getDegreeMasterData = (degree: string, course: string, type: string): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(`${GET_DEGREE_MASTER_DATA}?degree=${degree}&&courseName=${course}`, "GET", {
      "Content-Type": "application/json",
    })
      .then((res) => {
        dispatch({ type, payload: res?.result });
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const uploadDocument = (file: any, onSuccess: (res: any) => void): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    const formData = new FormData();
    formData.append("file", file);
    API_MIDDLEWARE(DOCUMENT_UPLOAD_URL, "POST", {}, formData, true)
      .then((res) => {
        onSuccess(res);
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const uploadImage = (file: any, onSuccess: (res: any) => void): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    const formData = new FormData();
    formData.append("file", file);
    API_MIDDLEWARE(IMAGE_UPLOAD_URL, "POST", {}, formData, true)
      .then((res) => {
        onSuccess(res);
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const getUserDetails = (type: string, field: string): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(GET_USER_DETAILS(field), "GET", { "Content-Type": "application/json" })
      .then((res) => {
        dispatch({ type, payload: res?.result });
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const getProfileDetails = (): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    return API_MIDDLEWARE(GET_USER_PROFILE_DETAILS, "GET", { "Content-Type": "application/json" })
      .then((res) => {
        dispatch({ type: SET_USER_DETAILS, payload: res?.result });
        dispatch(endAjaxCall());
        return res?.result;
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        throw err;
      });
  };
};

export const getQuestions = (): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(ASSESSMENT_READ_URL, "GET", { "Content-Type": "application/json" })
      .then((res) => {
        dispatch({ type: SET_QUESTIONS, payload: res?.result });
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const saveAnswer = (payload: any): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(ASSESSMENT_SAVE_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then(() => {
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const getPersonality = (applicant_id = ""): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(
      applicant_id ? `${B2B_USER_PERSONALITY}?applicant_id=${applicant_id}` : ASSESSMENT_PERSONALITY_TYPE_URL,
      "GET",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        dispatch({ type: SET_PERSONALITY, payload: res?.result });
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const getTopicScore = (applicant_id = ""): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(
      applicant_id ? `${B2B_TOPIC_SCORE}?applicant_id=${applicant_id}` : ASSESSMENT_TPOIC_SCORE_URL,
      "GET",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        dispatch({ type: SET_TOPIC_SCORE, payload: res?.result });
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const getScoreBand = (applicant_id = ""): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(applicant_id ? `${B2B_SCORE_BAND}?applicant_id=${applicant_id}` : ASSESSMENT_USER_BAND_URL, "GET", {
      "Content-Type": "application/json",
    })
      .then((res) => {
        dispatch({ type: SET_SCORE_BAND, payload: res?.result });
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
      });
  };
};

export const updatePromt = (payload: any): any => {
  return (dispatch: any) => {
    dispatch({ type: UPDATE_PROMPT, payload });
  };
};

export const saveUserVideos = (payload: any, onSuccess: any, onError: any): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(SAVE_USER_VIDEOS_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then(() => {
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch(() => {
        onError();
        dispatch(endAjaxCall());
      });
  };
};

export const getUserVideos = (setPrompts?: boolean, setSelectedFiles?: any): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(GET_USER_VIDEOS_URL, "GET", { "Content-Type": "application/json" })
      .then((res) => {
        dispatch({ type: SET_USER_VIDEOS, payload: res?.result });
        if (setPrompts) {
          const transformedData = {
            prompts: res?.result.reduce((acc: any, curr: any) => {
              acc[curr.topic] = {
                ...curr,
                name: curr?.videoPath,
              };
              return acc;
            }, {}),
          };
          setSelectedFiles(transformedData?.prompts);
          dispatch({ type: SET_PROMPTS, payload: transformedData?.prompts });
        }
        dispatch(endAjaxCall());
      })
      .catch(() => {
        dispatch(endAjaxCall());
      });
  };
};

export const getVideoQuestions = (): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(GET_VIDEO_QUESTIONS_URL, "GET", { "Content-Type": "application/json" })
      .then((res) => {
        dispatch({ type: SET_VIDEO_QUESTIONS, payload: res.result });
        dispatch(endAjaxCall());
      })
      .catch(() => {
        dispatch(endAjaxCall());
      });
  };
};

export const getPreSignedUrl = async (fileName: string) => {
  try {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    const response = await fetch(`${GET_PRESIGNED_VIDEO_URL}?filePath=${fileName}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const data = await response.json();
    return data.result; // Pre-signed URL for file upload
  } catch (error) {
    console.error("Error getting pre-signed URL:", error);
  }
};

export const updateUserVideos = (
  payload: {
    questionId: number;
    videoPath: string;
    videoPrivacy: string;
  }[],
  onSuccess: () => void,
  onError: () => void
): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(REPLACE_USER_VIDEO, "POST", { "Content-Type": "application/json" }, payload)
      .then(() => {
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch(() => {
        onError();
        dispatch(endAjaxCall());
      });
  };
};

export const deleteUserVideo = (questionId: any): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(DELETE_USER_VIDEO(questionId), "GET", { "Content-Type": "application/json" })
      .then(() => {
        dispatch(endAjaxCall());
      })
      .catch(() => {
        dispatch(endAjaxCall());
      });
  };
};

export const editUserProfileInfo = (payload: any, onSuccess = () => {}, onError = () => {}): any => {
  return (dispatch: any) => {
    dispatch(beginAjaxCall());
    API_MIDDLEWARE(USER_PROFILE_EDIT_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res) => {
        dispatch(fetchUserProfile());
        dispatch(setSuccessMessage(res?.message ?? ""));
        dispatch(setSuccessMessage(res?.message ?? "Profile updated successfully!"));
        onSuccess();
        dispatch(endAjaxCall());
      })
      .catch((err) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        dispatch(endAjaxCall());
        onError();
      });
  };
};
